import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { i18nService } from '../../services/i18n';
import { localizeUserFacingError } from '../../utils/localizedError';
import { skillService } from '../../services/skill';
import { setSkills } from '../../store/slices/skillSlice';
import { RootState } from '../../store';
import { OnlineSkill } from '../../types/skill';
import { getLocalizedOnlineSkillSummary, getLocalizedOnlineSkillTag } from '../../utils/localizedOnlineSkillMetadata';
import OnlineSkillCard from './OnlineSkillCard';
import SkillDetailDialog from './SkillDetailDialog';

const PAGE_SIZE = 24;

const FeaturedSkillsPanel: React.FC = () => {
  const dispatch = useDispatch();
  const installedSkills = useSelector((state: RootState) => state.skill.skills);
  const [skills, setFeaturedSkills] = useState<OnlineSkill[]>([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [initialLoadError, setInitialLoadError] = useState('');
  const [loadMoreError, setLoadMoreError] = useState('');
  const [installError, setInstallError] = useState('');
  const [installStatus, setInstallStatus] = useState<Record<string, 'idle' | 'installing' | 'installed'>>({});
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedSkill, setSelectedSkill] = useState<OnlineSkill | null>(null);
  const [language, setLanguage] = useState(i18nService.getLanguage());
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const requestIdRef = useRef(0);

  /** 与服务端安装预检保持一致：展示名称可重名、可本地化，不可作为技能身份。 */
  const isAlreadyInstalled = useCallback((slug: string) => installedSkills.some(skill => skill.id === slug), [installedSkills]);

  const fetchSkills = useCallback(async (searchQuery: string, pageNum: number, append: boolean) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setInstallError('');
    if (append) {
      setLoadMoreError('');
    } else {
      setInitialLoadError('');
      setLoadMoreError('');
      setFeaturedSkills([]);
      setPage(1);
      setTotal(0);
      setTotalPages(1);
    }

    const result = await skillService.fetchFeaturedSkills({
      search: searchQuery,
      page: pageNum,
      limit: PAGE_SIZE,
    });
    if (requestId !== requestIdRef.current) return;

    setLoading(false);
    if (result.success && result.data) {
      if (append) {
        setFeaturedSkills(prev => [...prev, ...result.data!.skills]);
      } else {
        setFeaturedSkills(result.data.skills);
      }
      setPage(pageNum);
      setTotal(result.data.total);
      setTotalPages(result.data.totalPages);
      setInitialLoadError('');
      setLoadMoreError('');
    } else {
      const message = localizeUserFacingError(result.error, 'skillsFeaturedError');
      if (append) {
        setLoadMoreError(message);
      } else {
        setFeaturedSkills([]);
        setPage(1);
        setTotal(0);
        setTotalPages(1);
        setInitialLoadError(message);
      }
    }
  }, [language]);

  useEffect(() => i18nService.subscribe(() => {
    setLanguage(i18nService.getLanguage());
    setSelectedCategory('all');
    setInitialLoadError('');
    setLoadMoreError('');
    setInstallError('');
  }), []);

  useEffect(() => {
    fetchSkills('', 1, false);
  }, [fetchSkills]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setSelectedCategory('all');
      void fetchSkills(value, 1, false);
    }, 400);
  };

  const handleLoadMore = useCallback(() => {
    if (loading || loadMoreError || page >= totalPages) return;
    void fetchSkills(search, page + 1, true);
  }, [fetchSkills, loadMoreError, loading, page, search, totalPages]);

  const handleRetryLoadMore = useCallback(() => {
    if (loading || page >= totalPages) return;
    void fetchSkills(search, page + 1, true);
  }, [fetchSkills, loading, page, search, totalPages]);

  // 滚动到底自动加载下一页（替代手动"加载更多"按钮）。
  useEffect(() => {
    const target = loadMoreRef.current;
    if (!target || loading || page >= totalPages) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) handleLoadMore();
    }, { rootMargin: '320px 0px' });
    observer.observe(target);
    return () => observer.disconnect();
  }, [handleLoadMore, loading, page, totalPages]);

  const handleInstall = async (slug: string) => {
    if (installStatus[slug] === 'installing' || installStatus[slug] === 'installed') return;
    if (isAlreadyInstalled(slug)) return;
    setInstallError('');
    setInstallStatus(prev => ({ ...prev, [slug]: 'installing' }));
    const result = await skillService.installOnlineSkill(slug, true);
    if (result.success) {
      setInstallError('');
      setInstallStatus(prev => ({ ...prev, [slug]: 'installed' }));
      if (result.skills) {
        dispatch(setSkills(result.skills));
      }
    } else {
      setInstallStatus(prev => ({ ...prev, [slug]: 'idle' }));
      setInstallError(localizeUserFacingError(result.error, 'skillDownloadFailed'));
    }
  };

  const categoryLabelMap: Record<string, string> = useMemo(() => ({
    '文档处理': i18nService.t('categoryDocuments'),
    '搜索资讯': i18nService.t('categorySearch'),
    '设计创作': i18nService.t('categoryDesign'),
    '效率工具': i18nService.t('categoryTools'),
    '写作助手': i18nService.t('categoryWriting'),
    '开发集成': i18nService.t('categoryDev'),
  }), [language]);

  const categories = useMemo(() => {
    const cats = new Set<string>();
    skills.forEach(skill => {
      if (skill.tag) cats.add(skill.tag);
    });
    return Array.from(cats);
  }, [skills]);

  const filteredSkills = useMemo(() => {
    if (selectedCategory === 'all') return skills;
    if (selectedCategory === 'uncategorized') return skills.filter(s => !s.tag);
    return skills.filter(s => s.tag === selectedCategory);
  }, [skills, selectedCategory]);

  const categoryChipClass = (active: boolean) => (
    `whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${active
      ? 'bg-claude-text text-white dark:bg-claude-darkText dark:text-claude-darkBg'
      : 'text-claude-textSecondary hover:bg-claude-surfaceHover hover:text-claude-text dark:text-claude-darkTextSecondary dark:hover:bg-claude-darkSurfaceHover dark:hover:text-claude-darkText'
    }`
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <p className="max-w-2xl text-[13px] leading-5 text-claude-textSecondary dark:text-claude-darkTextSecondary">
          {i18nService.t('skillsFeaturedSubtitle')}
        </p>
        <span className="shrink-0 text-xs tabular-nums text-claude-textSecondary/75 dark:text-claude-darkTextSecondary/75">
          {filteredSkills.length} / {total}
        </span>
      </div>

      <div className="flex items-center gap-2.5">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-claude-textSecondary dark:text-claude-darkTextSecondary" />
          <input
            type="text"
            placeholder={i18nService.t('skillsFeaturedSearch')}
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="h-9 w-full rounded-lg border border-claude-border bg-white pl-9 pr-3 text-[13px] text-claude-text shadow-[0_1px_2px_rgba(0,0,0,0.02)] outline-none transition-[border-color,box-shadow] placeholder:text-claude-textSecondary/70 focus:border-claude-accent/60 focus:ring-4 focus:ring-claude-accent/10 dark:border-claude-darkBorder dark:bg-claude-darkSurface dark:text-claude-darkText dark:placeholder:text-claude-darkTextSecondary/65"
          />
        </div>
      </div>

      {categories.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            onClick={() => setSelectedCategory('all')}
            className={categoryChipClass(selectedCategory === 'all')}
          >
            {i18nService.t('allCategories')}
          </button>
          {categories.map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => setSelectedCategory(cat)}
              className={categoryChipClass(selectedCategory === cat)}
            >
              {categoryLabelMap[cat] || cat}
            </button>
          ))}
          {skills.some(s => !s.tag) && (
            <button
              type="button"
              onClick={() => setSelectedCategory('uncategorized')}
              className={categoryChipClass(selectedCategory === 'uncategorized')}
            >
              {i18nService.t('uncategorized')}
            </button>
          )}
        </div>
      )}

      {initialLoadError ? (
        <div className="flex flex-col items-center gap-3 py-10 text-center">
          <p className="text-[13px] text-red-500 dark:text-red-400">{initialLoadError}</p>
          <button
            type="button"
            onClick={() => void fetchSkills(search, 1, false)}
            className="rounded-lg border border-claude-border bg-white px-3 py-1.5 text-xs font-medium text-claude-text transition-colors hover:bg-claude-surfaceHover dark:border-claude-darkBorder dark:bg-claude-darkSurface dark:text-claude-darkText dark:hover:bg-claude-darkSurfaceHover"
          >
            {i18nService.t('retry')}
          </button>
        </div>
      ) : (
        <>
          {installError && (
            <div className="py-2 text-center text-[13px] text-red-500 dark:text-red-400">
              {installError}
            </div>
          )}

          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 2xl:grid-cols-3">
            {filteredSkills.length === 0 && !loading ? (
              <div className="col-span-full py-8 text-center text-sm text-claude-textSecondary dark:text-claude-darkTextSecondary">
                {i18nService.t('skillsFeaturedEmpty')}
              </div>
            ) : (
              filteredSkills.map((skill) => (
                <OnlineSkillCard
                  key={skill.slug}
                  skill={skill}
                  installStatus={installStatus[skill.slug] || (isAlreadyInstalled(skill.slug) ? 'installed' : 'idle')}
                  onInstall={handleInstall}
                  onOpenDetails={setSelectedSkill}
                />
              ))
            )}
          </div>

          {loading && (
            <div className="py-4 text-center text-[13px] text-claude-textSecondary dark:text-claude-darkTextSecondary">
              {i18nService.t('loading')}
            </div>
          )}

          {loadMoreError && (
            <div className="flex items-center justify-center gap-3 py-3 text-[13px]">
              <span className="text-red-500 dark:text-red-400">{loadMoreError}</span>
              <button
                type="button"
                onClick={handleRetryLoadMore}
                className="rounded-lg border border-claude-border bg-white px-3 py-1.5 text-xs font-medium text-claude-text transition-colors hover:bg-claude-surfaceHover dark:border-claude-darkBorder dark:bg-claude-darkSurface dark:text-claude-darkText dark:hover:bg-claude-darkSurfaceHover"
              >
                {i18nService.t('retry')}
              </button>
            </div>
          )}

          {skills.length > 0 && !loadMoreError && page < totalPages && (
            <div ref={loadMoreRef} className="h-px" aria-hidden="true" />
          )}
        </>
      )}

      <SkillDetailDialog
        item={selectedSkill ? {
          kind: 'online',
          skill: selectedSkill,
          summary: getLocalizedOnlineSkillSummary(selectedSkill),
          tagLabel: selectedSkill.tag ? getLocalizedOnlineSkillTag(selectedSkill.tag) : undefined,
          installStatus: installStatus[selectedSkill.slug]
            || (isAlreadyInstalled(selectedSkill.slug) ? 'installed' : 'idle'),
          onInstall: handleInstall,
        } : null}
        onClose={() => setSelectedSkill(null)}
      />
    </div>
  );
};

export default FeaturedSkillsPanel;
